import { useEffect, useState } from 'react'
import { wsService, type ConnectionState, type WebSocketMessage } from '../services/websocket'
import { useDispatchStore, handleWebSocketMessage } from '../stores/dispatchStore'
import { useNotificationStore } from '../stores/notificationStore'
import { useAuthStore } from '../stores/authStore'

/**
 * The shell owns the single WebSocket connection: connect once per
 * authenticated session, feed the dispatch store reducer, poll the unread
 * notification count, and disconnect on logout/unmount.
 */
export function useShellWebSocket(enabled: boolean): ConnectionState {
  const token = useAuthStore(s => s.token)
  const setWsConnected = useDispatchStore(s => s.setWsConnected)
  const fetchUnreadCount = useNotificationStore(s => s.fetchUnreadCount)
  const [state, setState] = useState<ConnectionState>(wsService.connectionState)

  useEffect(() => {
    if (!enabled || !token) return
    const handler = (message: WebSocketMessage) => handleWebSocketMessage(message, useDispatchStore.getState())
    const offMsg = wsService.on('*', handler)
    const offState = wsService.onStateChange((s) => { setState(s); setWsConnected(s === 'connected') })
    wsService.connect()
    return () => {
      offMsg()
      offState()
    }
  }, [enabled, token, setWsConnected])

  // Disconnect fully when the shell unmounts (logout) — not on page changes.
  useEffect(() => {
    if (!enabled) return
    return () => { wsService.disconnect() }
  }, [enabled])

  useEffect(() => {
    if (!enabled || !token) return
    fetchUnreadCount()
    const t = window.setInterval(fetchUnreadCount, 30_000)
    return () => window.clearInterval(t)
  }, [enabled, token, fetchUnreadCount])

  return state
}
